import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { TabsContent } from "../ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Edit2, Trash2, Tag, FolderPlus } from "lucide-react";

/**
 * CategoriesTab — Pestaña "Categorías" del catálogo.
 *
 * Extraída 1:1 desde `pages/CatalogPage.js`. Lista las categorías con sus bloques del
 * Package Builder, el número de servicios asociados y su estado; permite crear, editar
 * y eliminar (la eliminación se deshabilita si la categoría tiene servicios asociados).
 *
 * @param {Array}    props.categories             Categorías del catálogo.
 * @param {Array}    props.services               Servicios (para contar los asociados por categoría).
 * @param {Function} props.setCategoryModalOpen   Abre el modal de "Nueva Categoría".
 * @param {Function} props.getCategoryBlocks      (category) => string[] — bloques del Package Builder.
 * @param {Object}   props.BLOQUE_LABELS          Mapa valor→etiqueta legible de bloques.
 * @param {Function} props.setEditingCategory     Abre el modal de edición con la categoría dada.
 * @param {Function} props.canDelete              (resource) => boolean — permiso RBAC para eliminar.
 * @param {Function} props.handleDeleteCategory   (id) => void — elimina una categoría.
 */
export const CategoriesTab = ({
  categories,
  services,
  setCategoryModalOpen,
  getCategoryBlocks,
  BLOQUE_LABELS,
  setEditingCategory,
  canDelete,
  handleDeleteCategory,
}) => {
  return (
        <TabsContent value="categories" className="mt-6">
          <Card className="bg-[#0F0F3D] border-[#1E1E5F]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-white">Gestión de Categorías</CardTitle>
              <Button
                onClick={() => setCategoryModalOpen(true)}
                className="bg-[#42E695] text-[#04042C]"
                data-testid="create-category-btn"
              >
                <FolderPlus className="h-4 w-4 mr-2" />
                Nueva Categoría
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#1E1E5F]">
                    <TableHead className="text-[#03BEE4]">Nombre</TableHead>
                    <TableHead className="text-[#03BEE4]">Bloque</TableHead>
                    <TableHead className="text-[#03BEE4]">Servicios</TableHead>
                    <TableHead className="text-[#03BEE4]">Estado</TableHead>
                    <TableHead className="text-[#03BEE4]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((cat) => {
                    const serviceCount = services.filter(s => s.categoria === cat.name).length;
                    const catBlocks = getCategoryBlocks(cat);
                    return (
                      <TableRow key={cat.id} className="border-[#1E1E5F] hover:bg-[#1B1B4B]">
                        <TableCell className="font-medium text-white">
                          <div className="flex items-center gap-2">
                            <Tag className="h-4 w-4 text-[#03BEE4]" />
                            {cat.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          {catBlocks.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {catBlocks.map(b => (
                                <Badge key={b} className="bg-[#03BEE4]/15 text-[#03BEE4] text-xs">{BLOQUE_LABELS[b] || b}</Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-500 text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-300">{serviceCount}</TableCell>
                        <TableCell>
                          <Badge className={cat.is_active !== false ? "bg-[#42E695]/20 text-[#42E695]" : "bg-red-500/20 text-red-400"}>
                            {cat.is_active !== false ? "Activa" : "Inactiva"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingCategory({ ...cat, bloques_package_builder: getCategoryBlocks(cat) })}
                            >
                              <Edit2 className="h-4 w-4 text-[#03BEE4]" />
                            </Button>
                            {canDelete("catalog") && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCategory(cat.id)}
                              disabled={serviceCount > 0}
                              title={serviceCount > 0 ? "No se puede eliminar, tiene servicios asociados" : "Eliminar"}
                            >
                              <Trash2 className={`h-4 w-4 ${serviceCount > 0 ? "text-slate-500" : "text-red-400"}`} />
                            </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
  );
};
